import { CalibrationDisplaySchema, CalibrationPointsConfig, PiezometerRawFrequencyPoint, DisplayLabelContext } from "../types";
import {
  formatScientific,
  formatGaugeFactor,
  formatTempCoeff,
  formatPercentFS,
  formatDigit,
  formatPressureMPa,
} from "../formatters";
import { normalizeCategory, matchesCategory } from "../categoryUtils";

const isPiezometerCategory = (data: any): boolean =>
  matchesCategory(normalizeCategory(data?.category), "piezometer");

/** Column/field labels that include sensor_type.unit (aligned with certificate PDF). */
const piezoPressureLabel = (ctx: DisplayLabelContext) => `Pressure (${ctx.measurementUnit})`;
const piezoEndFitLabel = (ctx: DisplayLabelContext) => `End Fit (${ctx.measurementUnit})`;
const piezoPolyFitLabel = (ctx: DisplayLabelContext) => `Poly Fit (${ctx.measurementUnit})`;
const piezoPerDegCLabel = (ctx: DisplayLabelContext) => `${ctx.measurementUnit}/°C`;

// Default calibration points for piezometer sensors
const defaultCalibrationPoints: CalibrationPointsConfig = {
  increasing: [10, 20, 30, 40, 50],
  decreasing: [40, 30, 20, 10, 0],
  secondIncreasing: [10, 20, 30, 40, 50]
};

// Generate placeholder value for a calibration point
const generatePiezometerPlaceholder = (
  point: number,
  sequence: "inc" | "dec" | "inc2"
): string => {
  const baseValue = 1800;
  if (sequence === "inc2") return `${baseValue + point * 5 + 0.2}`;
  return `${baseValue + point * 5}`;
};

// Format a step value without rounding to a fixed number of decimals,
// only trimming floating-point noise (e.g. 0.28 must stay "0.28", not "0.3").
const formatStepValue = (value: number): string => {
  const rounded = Math.round(value * 1e6) / 1e6;
  return rounded.toString();
};

// Generate template labels for piezometer sensors based on range
const generatePiezometerTemplateLabels = (
  range: string
): { frequencyLabels: string[]; temperatureLabels: string[] } | null => {
  // Extract numeric value from range string (e.g., "1MPa" -> 1, "0.5MPa" -> 0.5)
  const rangeMatch = range.match(/(\d+\.?\d*)/);
  if (!rangeMatch) return null;

  const rangeValue = parseFloat(rangeMatch[1]);
  if (isNaN(rangeValue) || rangeValue <= 0) return null;

  // Calculate step size (range divided by 5)
  const step = rangeValue / 5;

  // Generate frequency labels
  // Pattern: BOL, AOL, then increasing (step, 2*step, 3*step, 4*step, 5*step),
  // then decreasing (4*step, 3*step, 2*step, step, 0),
  // then second increasing (step, 2*step, 3*step, 4*step, 5*step)
  const frequencyLabels: string[] = ["BOL", "AOL"];

  // First increasing sequence
  for (let i = 1; i <= 5; i++) {
    frequencyLabels.push(formatStepValue(i * step));
  }

  // Decreasing sequence
  for (let i = 4; i >= 1; i--) {
    frequencyLabels.push(formatStepValue(i * step));
  }
  frequencyLabels.push("0");

  // Second increasing sequence
  for (let i = 1; i <= 5; i++) {
    frequencyLabels.push(formatStepValue(i * step));
  }

  // Temperature labels are always the same
  const temperatureLabels = ["Room T", "High T", "Room F", "High temp F"];

  return { frequencyLabels, temperatureLabels };
};

/**
 * Transform form data to raw frequency array for piezometer sensors
 */
const transformPiezometerToRawFrequency = (
  formData: Record<string, unknown>,
  rangeValue: number
): PiezometerRawFrequencyPoint[] => {
  const rawFrequencyPiezometer: PiezometerRawFrequencyPoint[] = [];
  const step = rangeValue / 5;
  const expectedSteps = [0, step, 2 * step, 3 * step, 4 * step, 5 * step];

  // Parse calibration field
  const getCalibrationPointValue = (fieldName: string): number | null => {
    const value = formData[fieldName];
    return typeof value === "string" && value.trim() !== "" ? parseFloat(value) : null;
  };

  // AOL should be the first entry in raw_frequency at pressure 0
  // BOL is handled separately in input_data.BOL, not in raw_frequency
  let aolFrequency: number | null = null;
  if (typeof formData.AOL === "string" && formData.AOL.trim() !== "") {
    const aolValue = parseFloat(formData.AOL);
    if (!isNaN(aolValue) && aolValue !== 0) {
      aolFrequency = aolValue;
    }
  }

  // Detect all calibration points
  const detectCalibrationPoints = (): number[] => {
    const points: number[] = [];

    Object.keys(formData).forEach(key => {
      const match = key.match(/^point_(\d+(?:_\d+)*)_(inc|dec|inc2)$|^point_0$/);
      if (!match) return;

      // Ignore empty fields. Leftover default-template fields (point_10_inc …
      // point_50_inc) are empty strings; including them pollutes the
      // index-based pressure mapping below and breaks any range whose real
      // points exceed the defaults (e.g. a 350 kPa sensor: 70..350).
      if (getCalibrationPointValue(key) === null) return;

      const pointValue =
        key === "point_0"
          ? 0
          : parseFloat(match[1].replace(/_/g, "."));

      if (!isNaN(pointValue) && pointValue >= 0) {
        if (!points.includes(pointValue)) points.push(pointValue);
      }
    });

    return points.sort((a, b) => a - b);
  };

  const availablePoints = detectCalibrationPoints();

  // First increasing sequence
  const firstIncreasingPoints = availablePoints.filter(point => {
    const name =
      point === 0 ? "point_0" : `point_${point.toString().replace(/\./g, "_")}_inc`;
    return getCalibrationPointValue(name) !== null;
  });

  const nonZeroPoints = firstIncreasingPoints.filter(p => p !== 0);

  nonZeroPoints.slice(0, 5).forEach((point, index) => {
    const fieldName = `point_${point.toString().replace(/\./g, "_")}_inc`;
    const freq = getCalibrationPointValue(fieldName);
    if (freq !== null) {
      rawFrequencyPiezometer.push({
        pressure: expectedSteps[index + 1],
        frequency: freq
      });
    }
  });

  // Decreasing sequence
  const decreasingPoints = availablePoints
    .filter(point => {
      const name =
        point === 0 ? "point_0" : `point_${point.toString().replace(/\./g, "_")}_dec`;
      return getCalibrationPointValue(name) !== null;
    })
    .sort((a, b) => b - a);

  decreasingPoints.forEach((point, idx) => {
    const name =
      point === 0 ? "point_0" : `point_${point.toString().replace(/\./g, "_")}_dec`;
    const freq = getCalibrationPointValue(name);
    if (freq !== null) {
      const stepIndex = Math.max(0, Math.min(5, decreasingPoints.length - 1 - idx));
      rawFrequencyPiezometer.push({
        pressure: expectedSteps[stepIndex],
        frequency: freq
      });
    }
  });

  // Second increasing sequence
  const secondIncreasingPoints = availablePoints.filter(point => {
    const name = `point_${point.toString().replace(/\./g, "_")}_inc2`;
    return getCalibrationPointValue(name) !== null;
  });

  const nonZeroSecondInc = secondIncreasingPoints.filter(p => p !== 0);

  nonZeroSecondInc.forEach(point => {
    const fieldName = `point_${point.toString().replace(/\./g, "_")}_inc2`;
    const freq = getCalibrationPointValue(fieldName);
    if (freq !== null) {
      const stepIndex = Math.min(nonZeroSecondInc.indexOf(point) + 1, 5);
      rawFrequencyPiezometer.push({
        pressure: expectedSteps[stepIndex],
        frequency: freq
      });
    }
  });

  // Add AOL as the first entry at pressure 0 if it exists
  if (aolFrequency !== null) {
    rawFrequencyPiezometer.unshift({
      pressure: 0,
      frequency: aolFrequency
    });
  }

  return rawFrequencyPiezometer;
};


export const PiezometerSchema: CalibrationDisplaySchema = {
  sensorType: "piezometer",
  unit: "MPa",

  inputFieldConfig: {
    basicFields: ["BOL", "AOL"],
    calibrationPoints: defaultCalibrationPoints,
    temperatureFields: ['roomT', 'roomF', 'highT', 'highTempF'],
    additionalFields: [
      {
        key: 'dwtTraceable',
        label: 'DWT Traceable to Certificate Number',
        type: 'text',
        unit: '',
        placeholder: 'Certificate number',
        required: true, // Mandatory for Piezometer
        condition: (data: any) => {
          return isPiezometerCategory(data);
        }
      }
    ],
    generatePlaceholder: generatePiezometerPlaceholder,
    transformToRawFrequency: transformPiezometerToRawFrequency,
    generateTemplateLabels: generatePiezometerTemplateLabels
  },

  certificateFormConfig: {
    title: "Piezometer Certificate Details",
    fields: [
      {
        key: "date",
        label: "Date",
        type: "date",
        placeholder: "DD.MM.YYYY"
      },
      {
        key: "temperature",
        label: "Ambient Temperature",
        type: "text",
        placeholder: "e.g., 26 °C",
        unit: "°C"
      },
      {
        key: "atmPressure",
        label: "Atm. Pressure",
        type: "text",
        placeholder: "Enter value in unit shown",
        unit: (ctx) => ctx.measurementUnit,
      },
      {
        key: "cableLength",
        label: "Cable Length",
        type: "text",
        placeholder: "Leave blank if not applicable",
        unit: "Meter"
      },
      {
        key: "dwtNo",
        label: "DWT Traceable to Certificate Number",
        type: "text",
        placeholder: "Enter certificate number",
        required: true
      }
    ]
  },

  inputData: {
    sections: [
      {
        title: "Basic Calibration Information",
        bgColor: "bg-blue-50",
        group: "setup",
        fields: [
          {
            key: "raw_frequency.0.frequency",
            label: "AOL",
            unit: "",
            condition: (data: any) => {
              const first = data?.raw_frequency?.[0];
              if (!first) return false;
              const measurementValue = first.pressure ?? first.displacement;
              return measurementValue === 0 && first.frequency != null;
            }
          },
          { key: "gauge_factor", label: "Gauge Factor", unit: (ctx) => `${ctx.measurementUnit}/digit`, format: formatGaugeFactor },
          { key: "max_error_percent_fs", label: "Max Error %FS", unit: "%", format: formatPercentFS },
          { key: "max_error_poly_fit_percent_fs", label: "Max Error Poly Fit %FS", unit: "%", format: formatPercentFS }
        ]
      },
      {
        title: "ABC Coefficients",
        bgColor: "bg-gray-50",
        group: "setup",
        fields: [
          { key: "abc_coefficients.A", label: "Coefficient A", format: formatScientific },
          { key: "abc_coefficients.B", label: "Coefficient B", format: formatScientific },
          { key: "abc_coefficients.C", label: "Coefficient C", format: formatScientific }
        ],
        condition: (data: unknown) =>
          typeof data === "object" && data !== null && "abc_coefficients" in data
      },
      {
        title: "Temperature Compensation",
        bgColor: "bg-yellow-50",
        group: "setup",
        fields: [
          { key: "digit_per_deg", label: "Digit/°C", unit: "", format: formatTempCoeff },
          { key: "mpa_per_deg", label: piezoPerDegCLabel, unit: "", format: formatTempCoeff },
          { key: "percent_fs_per_deg", label: "%FS/°C", unit: "", format: formatTempCoeff }
        ],
        condition: (data: unknown) =>
          typeof data === "object" && data !== null && "digit_per_deg" in data
      },
      {
        title: "Error Analysis",
        bgColor: "bg-red-50",
        group: "analysis",
        fields: [
          {
            key: "max_repeatability_percent_fs",
            label: "Max Repeatability %FS",
            unit: "%",
            format: formatPercentFS
          },
          {
            key: "max_hysteresis_percent_fs",
            label: "Max Hysteresis %FS",
            unit: "%",
            format: formatPercentFS
          }
        ],
        condition: (data: unknown) =>
          typeof data === "object" &&
          data !== null &&
          ("max_repeatability_percent_fs" in data || "max_hysteresis_percent_fs" in data)
      },
      {
        title: "Environmental Conditions",
        bgColor: "bg-cyan-50",
        group: "setup",
        fields: [
          { key: "ambient_temperature", label: "Ambient Temperature", unit: "°C" },
          { key: "humidity", label: "Humidity", unit: "%" },
          {
            key: "atmospheric_pressure",
            label: "Atmospheric Pressure",
            unit: (ctx) => ctx.measurementUnit,
          },
          { key: "weather_condition", label: "Weather Condition", unit: "" },
        ],
        condition: (data: unknown) =>
          typeof data === "object" &&
          data !== null &&
          ("ambient_temperature" in data || "humidity" in data),
      },
    ],

    tables: [
      {
        title: "Temperature Frequency Data",
        bgColor: "bg-yellow-50",
        group: "measurement",
        dataKey: "temperature_frequency",
        columns: [
          { key: "temperature", label: "Temperature (°C)", unit: "" },
          { key: "frequency", label: "Frequency", unit: "" },
          { key: "condition", label: "Condition", unit: "" }
        ],
        condition: (data: unknown) =>
          typeof data === "object" && data !== null && "temperature_frequency" in data
      },
      {
        title: "Raw Frequency & Digits Data",
        bgColor: "bg-green-50",
        group: "measurement",
        dataKey: "raw_frequency",
        secondaryDataKey: "raw_digits",
        mergeOn: ["pressure", "cycle"],
        columns: [
          { key: "pressure", label: piezoPressureLabel, unit: "", format: formatPressureMPa },
          { key: "frequency", label: "Frequency", unit: "", className: "font-mono" },
          { key: "digit", label: "Digit", className: "font-mono", unit: "", format: formatDigit },
          { key: "cycle", label: "Cycle", unit: "" }
        ],
        rowClassName: (row: unknown) =>
          typeof row === "object" && row !== null && "cycle" in row && (row as any).cycle === "increment"
            ? "bg-blue-50"
            : "bg-orange-50",
        condition: (data: unknown) =>
          typeof data === "object" && data !== null && "raw_frequency" in data
      },
      {
        title: "Average Points Analysis",
        bgColor: "bg-indigo-50",
        group: "analysis",
        dataKey: "average_points",
        columns: [
          { key: "pressure", label: piezoPressureLabel, unit: "", className: "font-medium", format: formatPressureMPa },
          { key: "digit_avg", label: "Digit Avg", className: "font-mono", unit: "", format: formatDigit },
          { key: "relative_digit", label: "Relative Digit", className: "font-mono", unit: "", format: formatDigit },
          { key: "end_fit_pressure", label: piezoEndFitLabel, className: "font-mono", unit: "", format: formatPressureMPa },
          {
            key: "error_end_fit_percent_fs",
            label: "Error End Fit %FS",
            unit: "%",
            className: "font-mono",
            format: formatPercentFS
          },
          {
            key: "poly_fit_pressure",
            label: piezoPolyFitLabel,
            unit: "",
            className: "font-mono",
            format: formatPressureMPa
          },
          {
            key: "error_poly_fit_percent_fs",
            label: "Error Poly Fit %FS",
            unit: "%",
            className: "font-mono",
            format: formatPercentFS
          }
        ],
        condition: (data: unknown) =>
          typeof data === "object" && data !== null && "average_points" in data
      }
    ]
  },

  outputData: {
    sections: [
      {
        title: "Summary",
        bgColor: "bg-green-50",
        group: "result",
        fields: [
          {
            key: "raw_frequency.0.frequency",
            label: "AOL",
            unit: "",
            condition: (data: any) => {
              const first = data?.raw_frequency?.[0];
              if (!first) return false;
              const measurementValue = first.pressure ?? first.displacement;
              return measurementValue === 0 && first.frequency != null;
            }
          },
          { key: "gauge_factor", label: "Gauge Factor", unit: (ctx) => `${ctx.measurementUnit}/digit`, format: formatGaugeFactor },
          { key: "Nl_percent_fs", label: "Nonlinearity %FS", unit: "%", format: formatPercentFS },
          { key: "Acc_percent_fs", label: "Accuracy %FS", unit: "%", format: formatPercentFS },
          { key: "Rep_percent_fs", label: "Repeatability %FS", unit: "%", format: formatPercentFS },
          { key: "Hys_percent_fs", label: "Hysteresis %FS", unit: "%", format: formatPercentFS },
          { key: "MPa_per_degC", label: piezoPerDegCLabel, unit: "", format: formatTempCoeff },
          { key: "percent_fs_per_degC", label: "%FS/°C", unit: "", format: formatTempCoeff }
        ]
      },
      {
        title: "ABC Coefficients",
        bgColor: "bg-blue-50",
        group: "result",
        fields: [
          { key: "A", label: "Coefficient A", unit: "", format: formatScientific },
          { key: "B", label: "Coefficient B", unit: "", format: formatScientific },
          { key: "C", label: "Coefficient C", unit: "", format: formatScientific }
        ]
      }
    ],

    tables: [
      {
        title: "Verification Data",
        bgColor: "bg-purple-50",
        group: "measurement",
        dataKey: "verification_points",
        columns: [
          { key: "pressure", label: piezoPressureLabel, unit: "", format: formatPressureMPa },
          { key: "reading", label: "Reading", unit: "", className: "font-mono" },
          { key: "deviation", label: "Deviation (%)", unit: "%", className: "font-mono", format: formatPercentFS }
        ],
        condition: (data: unknown) =>
          typeof data === "object" && data !== null && "verification_points" in data
      }
    ]
  }
};
